import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { validateCodiceFiscale, validatePartitaIVA } from "@/lib/validators";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, FileImage, Users, Plus, Pencil, UserX, UserCheck, Save, Star, Trash2, Shield, Link2, Unlink, User, Lock } from "lucide-react";
import { toast } from "sonner";

function OrgSettings() {
  const [form, setForm] = useState({ name: "", address: "", phone: "", email: "", fiscal_code: "", vat_number: "", bank_accounts: [] });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/organization").then(({ data }) => setForm({ ...data, bank_accounts: data.bank_accounts || [] })).catch(() => { toast.error("Errore caricamento organizzazione"); });
  }, []);

  const validate = () => {
    const e = {};
    const cfResult = validateCodiceFiscale(form.fiscal_code);
    if (!cfResult.valid) e.fiscal_code = cfResult.error;
    const pivaResult = validatePartitaIVA(form.vat_number);
    if (!pivaResult.valid) e.vat_number = pivaResult.error;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await api.put("/organization", form);
      toast.success("Impostazioni aggiornate");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const F = (name, label) => (
    <div className="space-y-1">
      <Label className="text-xs text-zinc-500">{label}</Label>
      <Input
        value={form[name] || ""}
        onChange={(e) => { setForm({ ...form, [name]: e.target.value }); setErrors({ ...errors, [name]: undefined }); }}
        className={`rounded-sm ${errors[name] ? "border-red-400" : ""}`}
        data-testid={`org-${name}`}
      />
      {errors[name] && <p className="text-xs text-red-500">{errors[name]}</p>}
    </div>
  );

  return (
    <div className="card-surface p-6 space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {F("name", "Nome Azienda")}
        {F("email", "Email")}
        {F("address", "Indirizzo")}
        {F("phone", "Telefono")}
        {F("fiscal_code", "Codice Fiscale")}
        {F("vat_number", "Partita IVA")}
      </div>

      {/* Bank accounts */}
      <div className="border-t border-zinc-200 pt-4 space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Conti Correnti</Label>
          <Button size="sm" variant="outline" className="rounded-sm h-8"
            onClick={() => setForm({ ...form, bank_accounts: [...form.bank_accounts, { bank_name: "", iban: "" }] })}
            data-testid="add-bank-btn">
            <Plus className="w-3 h-3 mr-1" /> Aggiungi Banca
          </Button>
        </div>
        {form.bank_accounts.length === 0 && <p className="text-xs text-zinc-400">Nessun conto corrente configurato</p>}
        {form.bank_accounts.map((ba, idx) => (
          <div key={`ba-${ba.iban || idx}`} className="flex flex-col sm:flex-row gap-2 items-start sm:items-center p-3 bg-zinc-50 rounded-sm border border-zinc-200" data-testid={`bank-account-${idx}`}>
            <div className="flex-1 space-y-1 w-full sm:w-auto">
              <Label className="text-[10px] text-zinc-400 uppercase">Banca</Label>
              <Input placeholder="Nome banca" value={ba.bank_name}
                onChange={(e) => { const accs = [...form.bank_accounts]; accs[idx] = { ...accs[idx], bank_name: e.target.value }; setForm({ ...form, bank_accounts: accs }); }}
                className="rounded-sm" data-testid={`bank-name-${idx}`} />
            </div>
            <div className="flex-1 space-y-1 w-full sm:w-auto">
              <Label className="text-[10px] text-zinc-400 uppercase">IBAN</Label>
              <Input placeholder="IT00 X000 0000 0000 0000 0000 000" value={ba.iban}
                onChange={(e) => { const accs = [...form.bank_accounts]; accs[idx] = { ...accs[idx], iban: e.target.value.toUpperCase() }; setForm({ ...form, bank_accounts: accs }); }}
                className="rounded-sm font-mono text-sm" data-testid={`bank-iban-${idx}`} />
            </div>
            <button onClick={() => setForm({ ...form, bank_accounts: form.bank_accounts.filter((_, i) => i !== idx) })}
              className="p-2 hover:bg-red-50 rounded-sm mt-4 sm:mt-5 shrink-0" data-testid={`bank-delete-${idx}`}>
              <Trash2 className="w-4 h-4 text-red-500" />
            </button>
          </div>
        ))}
      </div>

      <Button onClick={handleSave} disabled={saving} className="bg-[#002FA7] hover:bg-[#002482] rounded-sm" data-testid="save-org-btn">
        <Save className="w-4 h-4 mr-1" /> {saving ? "Salvataggio..." : "Salva"}
      </Button>
    </div>
  );
}

function HeaderTemplates() {
  const [templates, setTemplates] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [isDefault, setIsDefault] = useState(false);

  const fetch = () => api.get("/header-templates").then(({ data }) => setTemplates(data)).catch(() => { toast.error("Errore caricamento template"); });
  useEffect(() => { fetch(); }, []);

  const handleSave = async () => {
    try {
      if (editing) {
        await api.put(`/header-templates/${editing.id}`, { name, is_default: isDefault });
      } else {
        await api.post("/header-templates", { name, is_default: isDefault });
      }
      toast.success("Template salvato");
      setDialogOpen(false);
      fetch();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare questo template?")) return;
    try { await api.delete(`/header-templates/${id}`); toast.success("Eliminato"); fetch(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const handleUpload = async (templateId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/header-templates/${templateId}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Immagine caricata");
      fetch();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-zinc-500">{templates.length} template</p>
        <Button size="sm" onClick={() => { setEditing(null); setName(""); setIsDefault(false); setDialogOpen(true); }} className="bg-[#002FA7] hover:bg-[#002482] rounded-sm" data-testid="add-template-btn">
          <Plus className="w-4 h-4 mr-1" /> Nuovo Template
        </Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {templates.map(t => (
          <div key={t.id} className="card-surface p-4 space-y-3" data-testid={`template-${t.id}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileImage className="w-4 h-4 text-zinc-400" />
                <span className="font-medium text-sm">{t.name}</span>
                {t.is_default && <Star className="w-3 h-3 text-amber-500 fill-amber-500" />}
              </div>
              <div className="flex gap-1">
                <button onClick={() => { setEditing(t); setName(t.name); setIsDefault(t.is_default); setDialogOpen(true); }} className="p-1.5 hover:bg-zinc-100 rounded-sm"><Pencil className="w-3 h-3" /></button>
                <button onClick={() => handleDelete(t.id)} className="p-1.5 hover:bg-red-50 rounded-sm"><Trash2 className="w-3 h-3 text-red-500" /></button>
              </div>
            </div>
            <label className="block">
              <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && handleUpload(t.id, e.target.files[0])} />
              <span className="text-xs text-[#002FA7] hover:underline cursor-pointer">{t.image_path ? "Cambia immagine" : "Carica immagine"}</span>
            </label>
          </div>
        ))}
      </div>
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle className="font-['Outfit']">{editing ? "Modifica Template" : "Nuovo Template"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1"><Label className="text-xs text-zinc-500">Nome</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-sm" data-testid="template-name-input" /></div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} /> Predefinito</label>
            <div className="flex gap-2 justify-end"><Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">Annulla</Button><Button onClick={handleSave} disabled={!name} className="bg-[#002FA7] hover:bg-[#002482] rounded-sm" data-testid="save-template-btn">Salva</Button></div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MoversSection() {
  const { user: currentUser } = useAuth();
  const [movers, setMovers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", password: "", confirmPassword: "", role: "MOVER", role_id: "" });

  const refetch = () => {
    api.get("/users").then(({ data }) => setMovers(data.filter(u => u.id !== currentUser?.id))).catch(() => { toast.error("Errore caricamento utenti"); });
    api.get("/roles").then(({ data }) => setRoles(data)).catch(() => { toast.error("Errore caricamento ruoli"); });
  };
  useEffect(() => { refetch(); }, []);

  const openCreate = () => {
    setEditing(null);
    const moverRole = roles.find(r => r.is_default_mover);
    setForm({ email: "", name: "", password: "", confirmPassword: "", role: "MOVER", role_id: moverRole?.id || "" });
    setDialogOpen(true);
  };

  const openEdit = (mover) => {
    setEditing(mover);
    setForm({ email: mover.email, name: mover.name, password: "", confirmPassword: "", role: mover.role || "MOVER", role_id: mover.role_id || "" });
    setDialogOpen(true);
  };

  const handleRoleSelect = (roleId) => {
    const role = roles.find(r => r.id === roleId);
    const baseRole = role?.is_default_owner ? "OWNER" : "MOVER";
    setForm({ ...form, role_id: roleId, role: baseRole });
  };

  const handleSave = async () => {
    if (!editing && form.password !== form.confirmPassword) {
      toast.error("Le password non coincidono");
      return;
    }
    try {
      if (editing) {
        const updates = { name: form.name, email: form.email, role: form.role, role_id: form.role_id || null };
        await api.put(`/users/${editing.id}`, updates);
        toast.success("Utente aggiornato");
      } else {
        await api.post("/users", { ...form });
        toast.success("Utente creato");
      }
      setDialogOpen(false);
      refetch();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm("Disattivare questo utente?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Disattivato"); refetch(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const handleReactivate = async (id) => {
    try {
      await api.put(`/users/${id}`, { is_active: true });
      toast.success("Utente riattivato");
      refetch();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const handleInlineRoleChange = async (userId, roleId) => {
    const role = roles.find(r => r.id === roleId);
    const baseRole = role?.is_default_owner ? "OWNER" : "MOVER";
    try {
      await api.put(`/users/${userId}`, { role: baseRole, role_id: roleId });
      toast.success("Ruolo aggiornato");
      refetch();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-zinc-500">{movers.length} utenti</p>
        <Button size="sm" onClick={openCreate} className="bg-[#002FA7] hover:bg-[#002482] rounded-sm" data-testid="add-mover-btn">
          <Plus className="w-4 h-4 mr-1" /> Nuovo Utente
        </Button>
      </div>
      <div className="card-surface overflow-hidden">
        <table className="w-full">
          <thead><tr className="bg-zinc-50 border-b border-zinc-200">
            <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider text-zinc-500">Nome</th>
            <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider text-zinc-500">Email</th>
            <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider text-zinc-500">Ruolo</th>
            <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider text-zinc-500">Stato</th>
            <th className="text-right px-5 py-3 text-xs font-semibold uppercase tracking-wider text-zinc-500">Azioni</th>
          </tr></thead>
          <tbody>
            {movers.map(m => (
              <tr key={m.id} className="border-b border-zinc-100" data-testid={`mover-row-${m.id}`}>
                <td className="px-5 py-3 text-sm font-medium">{m.name}</td>
                <td className="px-5 py-3 text-sm text-zinc-600">{m.email}</td>
                <td className="px-5 py-3">
                  <Select value={m.role_id || (m.role === "OWNER" ? "role_owner" : "role_mover")} onValueChange={(v) => handleInlineRoleChange(m.id, v)}>
                    <SelectTrigger className="h-7 w-36 rounded-sm text-xs" data-testid={`role-select-${m.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {roles.map(r => (
                        <SelectItem key={r.id} value={r.id}>{r.name}{r.is_system ? " (sistema)" : ""}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-5 py-3"><span className={`text-xs font-medium ${m.is_active ? "text-emerald-600" : "text-red-500"}`}>{m.is_active ? "Attivo" : "Disattivato"}</span></td>
                <td className="px-5 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => openEdit(m)} className="p-1.5 hover:bg-zinc-100 rounded-sm" data-testid={`edit-mover-${m.id}`} title="Modifica">
                      <Pencil className="w-3.5 h-3.5 text-zinc-500" />
                    </button>
                    {m.is_active ? (
                      <button onClick={() => handleDeactivate(m.id)} className="p-1.5 hover:bg-red-50 rounded-sm" data-testid={`deactivate-mover-${m.id}`} title="Disattiva">
                        <UserX className="w-3.5 h-3.5 text-red-500" />
                      </button>
                    ) : (
                      <button onClick={() => handleReactivate(m.id)} className="p-1.5 hover:bg-emerald-50 rounded-sm" data-testid={`reactivate-mover-${m.id}`} title="Riattiva">
                        <UserCheck className="w-3.5 h-3.5 text-emerald-600" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {movers.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-8 text-center text-sm text-zinc-400">Nessun utente</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle className="font-['Outfit']">{editing ? "Modifica Utente" : "Nuovo Utente"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1"><Label className="text-xs text-zinc-500">Nome</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" data-testid="mover-name-input" /></div>
            <div className="space-y-1"><Label className="text-xs text-zinc-500">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-sm" data-testid="mover-email-input" /></div>
            {!editing && (
              <>
                <div className="space-y-1"><Label className="text-xs text-zinc-500">Password</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-sm" data-testid="mover-password-input" /></div>
                <div className="space-y-1"><Label className="text-xs text-zinc-500">Conferma Password</Label><Input type="password" value={form.confirmPassword} onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })} className={`rounded-sm ${form.confirmPassword && form.password !== form.confirmPassword ? "border-red-400" : ""}`} data-testid="mover-confirm-password-input" /></div>
                {form.confirmPassword && form.password !== form.confirmPassword && (
                  <p className="text-xs text-red-500" data-testid="password-mismatch-error">Le password non coincidono</p>
                )}
              </>
            )}
            <div className="space-y-1">
              <Label className="text-xs text-zinc-500">Ruolo</Label>
              <Select value={form.role_id || (form.role === "OWNER" ? "role_owner" : "role_mover")} onValueChange={handleRoleSelect}>
                <SelectTrigger className="rounded-sm" data-testid="mover-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {roles.map(r => (
                    <SelectItem key={r.id} value={r.id}>{r.name}{r.is_system ? " (sistema)" : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">Annulla</Button>
              <Button
                onClick={handleSave}
                disabled={!form.name || !form.email || (!editing && (!form.password || form.password !== form.confirmPassword))}
                className="bg-[#002FA7] hover:bg-[#002482] rounded-sm"
                data-testid="save-mover-btn"
              >
                {editing ? "Aggiorna" : "Crea"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RolesSection() {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", permissions: [] });
  const [loading, setLoading] = useState(true);

  const refetch = async () => {
    try {
      const [rolesRes, permsRes] = await Promise.all([
        api.get("/roles"),
        api.get("/roles/permissions"),
      ]);
      setRoles(rolesRes.data);
      setPermissions(permsRes.data);
    } catch {
      toast.error("Errore nel caricamento dei ruoli");
    } finally { setLoading(false); }
  };
  useEffect(() => { refetch(); }, []);

  const groups = permissions.reduce((acc, p) => {
    if (!acc[p.group]) acc[p.group] = [];
    acc[p.group].push(p);
    return acc;
  }, {});

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", description: "", permissions: [] });
    setDialogOpen(true);
  };

  const openEdit = (role) => {
    setEditing(role);
    setForm({ name: role.name, description: role.description || "", permissions: [...(role.permissions || [])] });
    setDialogOpen(true);
  };

  const togglePerm = (key) => {
    setForm(prev => ({
      ...prev,
      permissions: prev.permissions.includes(key)
        ? prev.permissions.filter(p => p !== key)
        : [...prev.permissions, key]
    }));
  };

  const handleSave = async () => {
    try {
      if (editing) {
        await api.put(`/roles/${editing.id}`, form);
        toast.success("Ruolo aggiornato");
      } else {
        await api.post("/roles", form);
        toast.success("Ruolo creato");
      }
      setDialogOpen(false);
      refetch();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const handleResetDefaults = async () => {
    if (!editing?.is_system) return;
    if (!window.confirm(`Ripristinare i permessi predefiniti per il ruolo "${editing.name}"? Eventuali modifiche saranno perse.`)) return;
    try {
      const { data } = await api.post(`/roles/${editing.id}/reset`);
      setForm(prev => ({ ...prev, permissions: [...(data.permissions || [])] }));
      toast.success("Permessi predefiniti ripristinati");
      refetch();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const handleDelete = async (roleId) => {
    if (!window.confirm("Eliminare questo ruolo?")) return;
    try {
      await api.delete(`/roles/${roleId}`);
      toast.success("Ruolo eliminato");
      refetch();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (loading) return <div className="py-8 text-center text-sm text-zinc-400">Caricamento...</div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-zinc-500">{roles.length} ruoli</p>
        <Button size="sm" onClick={openCreate} className="bg-[#002FA7] hover:bg-[#002482] rounded-sm" data-testid="add-role-btn">
          <Plus className="w-4 h-4 mr-1" /> Nuovo Ruolo
        </Button>
      </div>

      <div className="grid gap-3">
        {roles.map(role => (
          <div key={role.id} className="card-surface p-4" data-testid={`role-card-${role.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold">{role.name}</h3>
                  {role.is_system && <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-500 font-medium">Sistema</span>}
                </div>
                {role.description && <p className="text-xs text-zinc-500 mt-0.5">{role.description}</p>}
                <p className="text-xs text-zinc-400 mt-1">{role.permissions?.length || 0} permessi</p>
              </div>
              <div className="flex gap-1">
                <button onClick={() => openEdit(role)} className="p-1.5 hover:bg-zinc-100 rounded-sm" data-testid={`edit-role-${role.id}`} title="Modifica permessi">
                  <Pencil className="w-3.5 h-3.5 text-zinc-500" />
                </button>
                {!role.is_system && (
                  <button onClick={() => handleDelete(role.id)} className="p-1.5 hover:bg-red-50 rounded-sm" data-testid={`delete-role-${role.id}`} title="Elimina ruolo">
                    <Trash2 className="w-3.5 h-3.5 text-red-500" />
                  </button>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-1 mt-2">
              {(role.permissions || []).slice(0, 8).map(p => (
                <span key={p} className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">{p}</span>
              ))}
              {(role.permissions || []).length > 8 && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-500">+{role.permissions.length - 8}</span>
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-['Outfit']">{editing ? (editing.is_system ? `Modifica Permessi: ${editing.name}` : "Modifica Ruolo") : "Nuovo Ruolo"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {editing?.is_system && (
              <div className="rounded-sm border border-amber-300 bg-amber-50/60 p-3 text-xs text-amber-800" data-testid="system-role-notice">
                Ruolo di sistema: il nome e la descrizione sono fissi.{editing.is_default_owner ? " I permessi 'Gestisci ruoli', 'Gestisci utenti' e 'Gestisci impostazioni' devono restare attivi per evitare il blocco dell'amministrazione." : ""}
              </div>
            )}
            <div className="space-y-1">
              <Label className="text-xs text-zinc-500">Nome Ruolo</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" data-testid="role-name-input" placeholder="Es: Caposquadra" disabled={editing?.is_system} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-zinc-500">Descrizione</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm" data-testid="role-desc-input" placeholder="Descrizione opzionale" disabled={editing?.is_system} />
            </div>
            <div className="space-y-3">
              <Label className="text-xs text-zinc-500">Permessi</Label>
              {Object.entries(groups).map(([group, perms]) => (
                <div key={group} className="border border-zinc-200 rounded-sm p-3">
                  <h4 className="text-xs font-semibold text-zinc-600 mb-2">{group}</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    {perms.map(p => {
                      const isOwnerCritical = editing?.is_default_owner && ["roles.manage", "users.manage", "settings.manage"].includes(p.key);
                      return (
                        <label key={p.key} className={`flex items-center gap-2 text-xs px-1.5 py-1 rounded ${isOwnerCritical ? "opacity-60 cursor-not-allowed" : "cursor-pointer hover:bg-zinc-50"}`} data-testid={`perm-${p.key}`}>
                          <input type="checkbox" checked={form.permissions.includes(p.key)} onChange={() => !isOwnerCritical && togglePerm(p.key)} disabled={isOwnerCritical}
                            className="rounded border-zinc-300 text-[#002FA7] focus:ring-[#002FA7]" />
                          {p.label}{isOwnerCritical && <span className="text-[9px] text-amber-600 ml-1">(obbligatorio)</span>}
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-2 justify-between items-center pt-2">
              <div>
                {editing?.is_system && (
                  <Button variant="outline" onClick={handleResetDefaults} className="rounded-sm text-xs" data-testid="reset-role-btn">
                    Ripristina predefiniti
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">Annulla</Button>
                <Button onClick={handleSave} disabled={!form.name.trim()} className="bg-[#002FA7] hover:bg-[#002482] rounded-sm" data-testid="save-role-btn">
                  {editing ? "Aggiorna" : "Crea"}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function OwnerProfile() {
  const [profile, setProfile] = useState({ name: "", email: "" });
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [saving, setSaving] = useState(false);
  const [changingPw, setChangingPw] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/auth/me").then(r => {
      setProfile({ name: r.data.name || "", email: r.data.email || "" });
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await api.put("/auth/me", profile);
      toast.success("Profilo aggiornato");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore");
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (pwForm.new_password !== pwForm.confirm_password) {
      toast.error("Le password non coincidono");
      return;
    }
    setChangingPw(true);
    try {
      await api.post("/auth/me/change-password", pwForm);
      toast.success("Password aggiornata");
      setPwForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore");
    } finally {
      setChangingPw(false);
    }
  };

  if (loading) return <div className="card-surface p-6 text-center text-sm text-zinc-400">Caricamento...</div>;

  return (
    <div className="space-y-6 max-w-xl">
      {/* Profile info */}
      <div className="card-surface p-6 space-y-4">
        <h3 className="text-sm font-semibold font-['Outfit'] text-zinc-700">Dati Profilo</h3>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs text-zinc-500">Nome</Label>
            <Input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })}
              className="rounded-sm" data-testid="owner-name-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-zinc-500">Email</Label>
            <Input type="email" value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value })}
              className="rounded-sm" data-testid="owner-email-input" />
          </div>
        </div>
        <Button onClick={handleSaveProfile} disabled={saving || !profile.name || !profile.email}
          className="bg-[#002FA7] hover:bg-[#002482] rounded-sm" data-testid="save-owner-profile-btn">
          <Save className="w-4 h-4 mr-1" /> {saving ? "Salvataggio..." : "Salva Profilo"}
        </Button>
      </div>

      {/* Change password */}
      <div className="card-surface p-6 space-y-4">
        <h3 className="text-sm font-semibold font-['Outfit'] text-zinc-700">Cambia Password</h3>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs text-zinc-500">Password Attuale</Label>
            <Input type="password" value={pwForm.current_password}
              onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })}
              className="rounded-sm" data-testid="owner-current-password" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-zinc-500">Nuova Password</Label>
            <Input type="password" value={pwForm.new_password}
              onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })}
              className="rounded-sm" data-testid="owner-new-password" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-zinc-500">Conferma Nuova Password</Label>
            <Input type="password" value={pwForm.confirm_password}
              onChange={(e) => setPwForm({ ...pwForm, confirm_password: e.target.value })}
              className={`rounded-sm ${pwForm.confirm_password && pwForm.new_password !== pwForm.confirm_password ? "border-red-400" : ""}`}
              data-testid="owner-confirm-password" />
          </div>
          {pwForm.confirm_password && pwForm.new_password !== pwForm.confirm_password && (
            <p className="text-xs text-red-500">Le password non coincidono</p>
          )}
        </div>
        <Button onClick={handleChangePassword}
          disabled={changingPw || !pwForm.current_password || !pwForm.new_password || pwForm.new_password !== pwForm.confirm_password}
          variant="outline" className="rounded-sm" data-testid="change-password-btn">
          <Lock className="w-4 h-4 mr-1" /> {changingPw ? "Aggiornamento..." : "Cambia Password"}
        </Button>
      </div>
    </div>
  );
}

function GoogleConnectionSection() {
  const [conn, setConn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);

  const fetchConnection = async () => {
    try {
      const { data } = await api.get("/google/connection");
      setConn(data);
    } catch {
      setConn({ connected: false });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConnection();

    const handleMessage = async (event) => {
      if (event.data?.type === "google-oauth-callback" && event.data?.code) {
        setConnecting(true);
        try {
          await api.post("/google/callback", {
            code: event.data.code,
            redirect_uri: event.data.redirect_uri,
          });
          toast.success("Account Google collegato!");
          fetchConnection();
        } catch (err) {
          toast.error(formatApiError(err.response?.data?.detail) || "Errore durante il collegamento");
        } finally {
          setConnecting(false);
        }
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      // REMINDER: DO NOT HARDCODE THE URL
      const redirectUri = window.location.origin + "/google-callback";
      const { data } = await api.get(`/google/auth-url?redirect_uri=${encodeURIComponent(redirectUri)}`);
      const popup = window.open(data.auth_url, "google-auth", "width=500,height=600,menubar=no,toolbar=no");
      if (!popup) {
        toast.error("Popup bloccato. Abilita i popup per collegare Google.");
        setConnecting(false);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore");
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Disconnettere l'account Google?")) return;
    try {
      await api.delete("/google/disconnect");
      toast.success("Account Google disconnesso");
      setConn({ connected: false });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (loading) return <div className="card-surface p-6 text-center text-sm text-zinc-400">Caricamento...</div>;

  return (
    <div className="card-surface p-6 space-y-4">
      <h3 className="text-sm font-semibold font-['Outfit'] text-zinc-700">Collegamento Account Google</h3>
      <p className="text-xs text-zinc-500">Collega il tuo account Google per esportare PDF su Google Drive e creare bozze email in Gmail.</p>
      {conn?.connected ? (
        <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-sm border border-emerald-200">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
              <Link2 className="w-4 h-4 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm font-medium">{conn.google_name || conn.google_email}</p>
              <p className="text-xs text-zinc-500">{conn.google_email}</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={handleDisconnect} className="rounded-sm text-red-500 border-red-200 hover:bg-red-50"
            data-testid="disconnect-google-btn">
            <Unlink className="w-3.5 h-3.5 mr-1" /> Disconnetti
          </Button>
        </div>
      ) : (
        <Button onClick={handleConnect} disabled={connecting} className="bg-[#002FA7] hover:bg-[#002482] rounded-sm"
          data-testid="connect-google-btn">
          <Link2 className="w-4 h-4 mr-1" /> {connecting ? "Collegamento in corso..." : "Collega Account Google"}
        </Button>
      )}
    </div>
  );
}

export default function Settings() {
  return (
    <div data-testid="settings-page" className="space-y-6 animate-fade-in">
      <h1 className="text-2xl font-bold tracking-tighter font-['Outfit']">Impostazioni</h1>
      <Tabs defaultValue="organization" className="space-y-6">
        <TabsList className="bg-zinc-100 rounded-sm">
          <TabsTrigger value="organization" className="rounded-sm data-[state=active]:bg-white" data-testid="tab-organization">
            <Building2 className="w-4 h-4 mr-1" /> Organizzazione
          </TabsTrigger>
          <TabsTrigger value="templates" className="rounded-sm data-[state=active]:bg-white" data-testid="tab-templates">
            <FileImage className="w-4 h-4 mr-1" /> Template
          </TabsTrigger>
          <TabsTrigger value="owner" className="rounded-sm data-[state=active]:bg-white" data-testid="tab-owner">
            <User className="w-4 h-4 mr-1" /> Proprietario
          </TabsTrigger>
          <TabsTrigger value="movers" className="rounded-sm data-[state=active]:bg-white" data-testid="tab-movers">
            <Users className="w-4 h-4 mr-1" /> Operatori
          </TabsTrigger>
          <TabsTrigger value="roles" className="rounded-sm data-[state=active]:bg-white" data-testid="tab-roles">
            <Shield className="w-4 h-4 mr-1" /> Ruoli
          </TabsTrigger>
          <TabsTrigger value="google" className="rounded-sm data-[state=active]:bg-white" data-testid="tab-google">
            <Link2 className="w-4 h-4 mr-1" /> Google
          </TabsTrigger>
        </TabsList>
        <TabsContent value="organization"><OrgSettings /></TabsContent>
        <TabsContent value="templates"><HeaderTemplates /></TabsContent>
        <TabsContent value="owner"><OwnerProfile /></TabsContent>
        <TabsContent value="movers"><MoversSection /></TabsContent>
        <TabsContent value="roles"><RolesSection /></TabsContent>
        <TabsContent value="google"><GoogleConnectionSection /></TabsContent>
      </Tabs>
    </div>
  );
}
